import { CoursePrice } from '@/app/components/CoursePrice'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { useCohortHelpers } from '@/app/hooks/useCohortHelpers'
import { useAuth } from '@/app/providers/Auth/Auth'
import { ICourse } from '@/types/course'
import { useTranslations } from 'next-intl'
import { useFormContext } from 'react-hook-form'
import { MdArrowForward } from 'react-icons/md'
import { CoursePageFormValues } from '../pages/CoursePage'

type CoursePriceBarProps = {
  courseInfo: Required<Pick<ICourse, 'educationProvider' | 'cohorts' | '_id'>>
  onApply: (cohortId: string) => Promise<void>
  onCohortSelection: () => void
  disabled?: boolean
  isLoading?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export function CoursePriceBar({
  courseInfo,
  onApply,
  onCohortSelection,
  disabled,
  isLoading,
  ...props
}: CoursePriceBarProps) {
  const { watch } = useFormContext<CoursePageFormValues>()

  const selectedCohortId = watch('selectedCohortId')

  const user = useAuth()
  const t = useTranslations('CoursePage')
  const { cohorts } = courseInfo || {}
  const { isCohortActive, hasValidCost } = useCohortHelpers()

  const hasMultipleCohorts = Array.isArray(cohorts) && cohorts.length > 1
  const hasCohorts = Array.isArray(cohorts) && cohorts.length > 0
  const hasNoActiveCohorts = cohorts.every((cohort) => !isCohortActive(cohort))
  const activeCohortsWithValidCost = cohorts.filter(hasValidCost)

  const hasActiveCohortsWithValidCost =
    Array.isArray(activeCohortsWithValidCost) &&
    activeCohortsWithValidCost.length > 0

  if (hasNoActiveCohorts) {
    return (
      <div
        {...props}
        className={`border-t border-t-lines bg-white px-8 py-4 ${props.className}`}
      >
        <p className="text-xl font-semibold text-[--text]">
          {t('new_cohorts_coming_soon')}
        </p>
      </div>
    )
  }

  return (
    <div
      {...props}
      className={`flex items-center border-t border-t-lines bg-white px-8 py-4 ${props.className}`}
    >
      {hasCohorts && (
        <>
          <CoursePrice
            course={courseInfo}
            selectedCohortId={selectedCohortId}
            className="font-semibold leading-8 tracking-tight"
            amountProps={{ className: '!text-3xl' }}
          />
        </>
      )}
      <div className="ml-auto flex flex-col gap-2 md:flex-row">
        {hasMultipleCohorts ? (
          <PrimaryButton
            id={`course-price-bar-button--${user?.isAuthenticated ? 'redirect-to-checkout' : 'redirect-to-signup'}`}
            type="button"
            className="ml-auto min-h-12 shrink-0 self-end font-medium"
            onClick={onCohortSelection}
            disabled={isLoading || !hasCohorts || disabled}
          >
            {user?.isAuthenticated && !isLoading
              ? t('selectCohort')
              : t('sign_up_to_apply')}
          </PrimaryButton>
        ) : hasNoActiveCohorts || !hasActiveCohortsWithValidCost ? (
          <PrimaryButton
            type="button"
            className="ml-auto min-h-12 shrink-0 self-end font-medium"
            disabled
          >
            {t('no_cohorts_available')}
          </PrimaryButton>
        ) : (
          <PrimaryButton
            type="button"
            className="ml-auto min-h-12 shrink-0 self-end font-medium"
            onClick={() => onApply(selectedCohortId ?? '')}
            disabled={isLoading || !hasCohorts || disabled}
          >
            {user?.isAuthenticated && !isLoading ? (
              <span className="flex items-center gap-2">
                <span className="mx-auto text-sm">{t('continue')}</span>
                <MdArrowForward className="ml-auto h-4 w-4" />
              </span>
            ) : (
              t('sign_up_to_apply')
            )}
          </PrimaryButton>
        )}
      </div>
    </div>
  )
}
